import { Component, Input } from '@angular/core';
import { chain, some } from 'lodash';
import { PaymentBatchService } from '../../paymentBatch.service';
import { BusyIndicatorService, SnackBarService } from 'src/app/Busy-Indicator';
import { WeissmanModalService } from 'src/app/Compliance/WeissmanModalService';
import { ShippingMethodsModalComponent, ShippingMethodsModalParams } from './shippingMethodsModal.component';
import { FeatureFlagsService } from 'src/app/Common/FeatureFlags/feature-flags-service';

const CONSOLIDATED_FEED_TYPES = [
    Core.PaymentBatchFeedTypes.ConsolidatedTransmittal,
    Core.PaymentBatchFeedTypes.ConsolidatedExcel,
];

@Component({
    selector: 'payment-batch-output',
    templateUrl: 'paymentBatchOutput.component.html',
    standalone: false,
})
export class PaymentBatchOutputComponent {
    constructor(
        private readonly _paymentBatchService: PaymentBatchService,
        private readonly _busyIndicatorService: BusyIndicatorService,
        private readonly _modalService: WeissmanModalService,
        private readonly _snackBarService: SnackBarService,
        private readonly _featureFlagsService: FeatureFlagsService,
    ) {
        if (this._featureFlagsService.featureFlags?.enablePaymentBatchMultiFeedPackage) {
            this.feeds.push(
                {
                    type: Core.PaymentBatchFeedTypes.ConsolidatedTransmittal,
                    checked: false,
                    label: 'Consolidated Transmittals',
                },
                {
                    type: Core.PaymentBatchFeedTypes.ConsolidatedExcel,
                    checked: false,
                    label: 'Consolidated Excel',
                },
            );
        }
    }

    @Input() paymentBatch: Core.PaymentBatchModel;

    outputTypes = ['Excel', 'CSV'];
    selectedOutputType: string = 'Excel';

    feeds: {
        type: Core.PaymentBatchFeedTypes;
        checked: boolean;
        label: string;
    }[] = [
        {
            type: Core.PaymentBatchFeedTypes.FinalAPFeed,
            checked: false,
            label: 'AP Feed',
        },
        {
            type: Core.PaymentBatchFeedTypes.CheckFeed,
            checked: false,
            label: 'Check Feed',
        },
        {
            type: Core.PaymentBatchFeedTypes.CertificateOfMailLog,
            checked: false,
            label: 'USPS Certificate of Mailings',
        },
        {
            type: Core.PaymentBatchFeedTypes.CertificateOfMailSingle,
            checked: false,
            label: 'USPS Certificate of Mailings (Single)',
        },
        {
            type: Core.PaymentBatchFeedTypes.EnvelopeInserts,
            checked: false,
            label: 'Envelope Inserts',
        },
        {
            type: Core.PaymentBatchFeedTypes.ShippingMethods,
            checked: false,
            label: 'Mailing Excel Extract',
        },
    ];

    get isDownloadDisabled(): boolean {
        return !some(this.feeds, 'checked');
    }

    showShippingMethods(type: Core.PaymentBatchFeedTypes): boolean {
        return (
            (this.paymentBatch.paymentBatchTaskSeriesOptionId == Core.PaymentBatchTaskSeriesOptionEnum.EarlyApproval ||
                this.paymentBatch.paymentBatchTaskSeriesOptionId == Core.PaymentBatchTaskSeriesOptionEnum.Standard) &&
            this.paymentBatch.hasCheckFeed &&
            type === Core.PaymentBatchFeedTypes.ShippingMethods
        );
    }

    isDisabled(feedType: Core.PaymentBatchFeedTypes) {
        if (feedType === Core.PaymentBatchFeedTypes.FinalAPFeed) {
            return false;
        } else if (CONSOLIDATED_FEED_TYPES.includes(feedType)) {
            return !this.paymentBatch.hasAPFeed || !this.paymentBatch.userHasElevatedPermission;
        } else {
            return !this.paymentBatch.hasCheckFeed || !this.paymentBatch.userHasElevatedPermission;
        }
    }

    async launchShippingMethodsModal() {
        const params: ShippingMethodsModalParams = {
            paymentBatchId: this.paymentBatch.paymentBatchId,
        };

        const result = await this._modalService.showAsync(
            ShippingMethodsModalComponent,
            params,
            'modal-xl',
            // 'modal-xl modal-no-max-width'
        );
    }

    async download() {
        const feedTypes = chain(this.feeds)
            .filter((feed) => feed.checked)
            .map((feed) => {
                if (feed.type === Core.PaymentBatchFeedTypes.ShippingMethods && this.selectedOutputType === 'CSV') {
                    return Core.PaymentBatchFeedTypes.ShippingMethodsCSV;
                }

                return feed.type;
            })
            .value();

        if (!feedTypes.length) {
            return;
        }

        const busyRef = this._busyIndicatorService.show({ message: 'Setting LRP...' });

        try {
            const longRunningProcessId = await this._paymentBatchService.getOutputZip(
                this.paymentBatch.paymentBatchId,
                feedTypes,
            );
            this._snackBarService.addById(
                longRunningProcessId,
                Compliance.LongRunningProcessTypeEnum.PaymentBatchExportFeedsAndFiles,
            );
        } finally {
            busyRef.hide();
        }
    }
}
